import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Coins, TrendingUp, Shield, BookOpen, ArrowRight, CheckCircle2, ChevronDown, ChevronUp } from "lucide-react";
import { Link } from "react-router-dom";
import { API } from "../App";

const GoldGuide = () => {
  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState({});
  const [goldPrice, setGoldPrice] = useState(null);
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [guideRes, marketRes] = await Promise.all([
          axios.get(`${API}/gold-guide`),
          axios.get(`${API}/market/commodities`)
        ]);
        setGuide(guideRes.data);
        
        // Find gold in commodities
        const gold = marketRes.data.data?.find(c => c.symbol === 'XAU');
        setGoldPrice(gold);
      } catch (error) {
        console.error("Error fetching gold guide:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);
  
  const toggleSection = (index) => {
    setExpandedSections(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };
  
  const renderMarkdown = (content) => {
    if (!content) return null;
    
    const lines = content.split('\n');
    return lines.map((line, index) => {
      if (line.startsWith('## ')) {
        return <h2 key={index}>{line.slice(3)}</h2>;
      } else if (line.startsWith('### ')) {
        return <h3 key={index}>{line.slice(4)}</h3>;
      } else if (line.startsWith('- ')) {
        return <li key={index}>{formatInline(line.slice(2))}</li>;
      } else if (line.match(/^\d+\. /)) {
        return <li key={index}>{formatInline(line.replace(/^\d+\. /, ''))}</li>;
      } else if (line.trim()) {
        return <p key={index}>{formatInline(line)}</p>;
      }
      return null;
    });
  };
  
  const formatInline = (text) => {
    text = text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    return <span dangerouslySetInnerHTML={{ __html: text }} />;
  };
  
  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner" />
        <p>Loading Gold Trading Guide...</p>
      </div>
    );
  }
  
  return (
    <div className="gold-guide" data-testid="gold-guide">
      {/* Hero Section */}
      <section className="gold-hero" data-testid="gold-hero">
        <div className="hero-bg">
          <img 
            src="https://images.unsplash.com/photo-1755728806819-26a994fae81a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHxnb2xkJTIwYmFycyUyMGZpbmFuY2lhbCUyMGx1eHVyeXxlbnwwfHx8fDE3NzA1NTk4NDB8MA&ixlib=rb-4.1.0&q=85" 
            alt="Gold Bars" 
          />
          <div className="hero-overlay" />
        </div>
        <div className="container hero-content">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="hero-badge gold-badge">PRECIOUS METALS</span>
            <h1>Complete Gold Trading Guide</h1>
            <p>Learn how to trade gold like a professional - from basics to advanced strategies</p>
          </motion.div>
          
          {/* Gold Price Card */}
          {goldPrice && (
            <motion.div 
              className="gold-price-card"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              data-testid="gold-price-card"
            >
              <div className="price-icon">
                <Coins size={32} />
              </div>
              <div className="price-info">
                <span className="label">Gold (XAU/USD)</span>
                <span className="price">${goldPrice.price?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                <span className={`change ${goldPrice.change_24h >= 0 ? 'positive' : 'negative'}`}>
                  {goldPrice.change_24h >= 0 ? '+' : ''}{goldPrice.change_24h?.toFixed(2)}% (24h)
                </span>
              </div>
            </motion.div>
          )}
        </div>
      </section>
      
      {/* Quick Facts */}
      <section className="section quick-facts" data-testid="quick-facts">
        <div className="container">
          <div className="facts-grid">
            <div className="fact-card">
              <Shield size={32} />
              <h4>Safe Haven Asset</h4>
              <p>Gold typically rises during economic uncertainty and market crashes</p>
            </div>
            <div className="fact-card">
              <TrendingUp size={32} />
              <h4>Inflation Hedge</h4>
              <p>Gold preserves purchasing power when currencies lose value</p>
            </div>
            <div className="fact-card">
              <BookOpen size={32} />
              <h4>5,000+ Year History</h4>
              <p>Gold has been used as money and store of value for millennia</p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Guide Content */}
      <section className="section guide-content-section" data-testid="guide-content-section">
        <div className="container">
          <h2 className="section-title">The Complete Guide</h2>
          
          <div className="guide-sections">
            {guide?.sections?.map((section, index) => (
              <motion.div
                key={index}
                className="guide-section"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <button 
                  className={`section-header ${expandedSections[index] ? 'expanded' : ''}`}
                  onClick={() => toggleSection(index)}
                  data-testid={`section-toggle-${index}`}
                >
                  <div className="section-title-wrapper">
                    <span className="section-number">{String(index + 1).padStart(2, '0')}</span>
                    <h3>{section.title}</h3>
                  </div>
                  {expandedSections[index] ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
                </button>
                
                {expandedSections[index] && (
                  <motion.div 
                    className="section-content"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    data-testid={`section-content-${index}`}
                  >
                    {renderMarkdown(section.content)}
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Key Takeaways */}
      <section className="section takeaways-section" data-testid="takeaways-section">
        <div className="container">
          <h2 className="section-title">Key Takeaways</h2>
          
          <div className="takeaways-grid">
            {[
              "Start with paper trading to learn without risking real money",
              "Gold ETFs (GLD, IAU) are the easiest way for beginners",
              "Always use stop losses to protect your capital",
              "Watch the US Dollar - gold often moves inversely",
              "Never invest more than you can afford to lose",
              "Fed interest rate decisions significantly impact gold"
            ].map((takeaway, index) => (
              <motion.div 
                key={index}
                className="takeaway-item"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <CheckCircle2 size={20} />
                <span>{takeaway}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* CTA */}
      <section className="section cta-section" data-testid="cta-section">
        <div className="container">
          <div className="cta-card">
            <Coins size={48} className="cta-icon" />
            <h2>Ready to Practice Gold Trading?</h2>
            <p>Try our paper trading simulator with virtual money - includes gold and other commodities!</p>
            <Link to="/trade" className="btn btn-gold btn-pill" data-testid="try-paper-trading-btn">
              Start Paper Trading
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
      
      <style>{`
        .gold-guide {
          background: var(--bg-default);
        }
        
        /* Hero */
        .gold-hero {
          position: relative;
          min-height: 70vh;
          display: flex;
          align-items: center;
          padding: 120px 0 80px;
        }
        
        .hero-bg {
          position: absolute;
          inset: 0;
          z-index: 0;
        }
        
        .hero-bg img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          opacity: 0.4;
        }
        
        .hero-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(to bottom, rgba(10,10,10,0.6), var(--bg-default));
        }
        
        .hero-content {
          position: relative;
          z-index: 1;
        }
        
        .gold-badge {
          display: inline-block;
          padding: 8px 16px;
          background: rgba(255, 215, 0, 0.1);
          border: 1px solid rgba(255, 215, 0, 0.3);
          border-radius: 50px;
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 0.1em;
          color: var(--gold);
          margin-bottom: 24px;
        }
        
        .gold-hero h1 {
          font-size: 3.5rem;
          margin-bottom: 16px;
        }
        
        .gold-hero > .container > div > p {
          font-size: 1.25rem;
          color: var(--text-secondary);
          max-width: 600px;
        }
        
        /* Gold Price Card */
        .gold-price-card {
          display: inline-flex;
          align-items: center;
          gap: 20px;
          background: linear-gradient(135deg, rgba(255, 215, 0, 0.1), rgba(184, 134, 11, 0.1));
          border: 1px solid rgba(255, 215, 0, 0.2);
          border-radius: 16px;
          padding: 24px 32px;
          margin-top: 32px;
        }
        
        .price-icon {
          width: 64px;
          height: 64px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, var(--gold), var(--gold-dark));
          border-radius: 12px;
          color: var(--bg-default);
        }
        
        .price-info {
          display: flex;
          flex-direction: column;
        }
        
        .price-info .label {
          font-size: 14px;
          color: var(--text-muted);
        }
        
        .price-info .price {
          font-family: var(--font-mono);
          font-size: 2rem;
          font-weight: 700;
          color: var(--gold);
        }
        
        .price-info .change {
          font-family: var(--font-mono);
          font-size: 14px;
        }
        
        .change.positive { color: var(--success); }
        .change.negative { color: var(--error); }
        
        /* Quick Facts */
        .quick-facts {
          background: var(--bg-paper);
        }
        
        .facts-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
        }
        
        .fact-card {
          text-align: center;
          padding: 32px;
        }
        
        .fact-card svg {
          color: var(--gold);
          margin-bottom: 16px;
        }
        
        .fact-card h4 {
          font-size: 1.25rem;
          margin-bottom: 8px;
        }
        
        .fact-card p {
          color: var(--text-secondary);
          font-size: 14px;
        }
        
        /* Guide Content */
        .section-title {
          text-align: center;
          font-size: 2rem;
          margin-bottom: 40px;
        }
        
        .guide-sections {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
        
        .guide-section {
          background: var(--bg-paper);
          border: 1px solid var(--border);
          border-radius: 12px;
          overflow: hidden;
        }
        
        .section-header {
          width: 100%;
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          background: transparent;
          border: none;
          color: var(--text-primary);
          cursor: pointer;
          transition: background-color 0.2s;
        }
        
        .section-header:hover {
          background: var(--bg-subtle);
        }
        
        .section-header.expanded {
          border-bottom: 1px solid var(--border);
        }
        
        .section-title-wrapper {
          display: flex;
          align-items: center;
          gap: 16px;
        }
        
        .section-number {
          font-family: var(--font-mono);
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--gold);
        }
        
        .section-header h3 {
          font-size: 1.125rem;
          margin: 0;
        }
        
        .section-content {
          padding: 24px;
          overflow: hidden;
        }
        
        .section-content h2 {
          font-size: 1.25rem;
          color: var(--gold);
          margin-top: 1.5rem;
          margin-bottom: 0.75rem;
        }
        
        .section-content h2:first-child {
          margin-top: 0;
        }
        
        .section-content h3 {
          font-size: 1rem;
          margin-top: 1.25rem;
          margin-bottom: 0.5rem;
        }
        
        .section-content p {
          color: var(--text-secondary);
          margin-bottom: 0.75rem;
        }
        
        .section-content li {
          color: var(--text-secondary);
          margin-bottom: 0.5rem;
          padding-left: 20px;
          position: relative;
        }
        
        .section-content li::before {
          content: "";
          position: absolute;
          left: 0;
          top: 10px;
          width: 6px;
          height: 6px;
          background: var(--gold);
          border-radius: 50%;
        }
        
        .section-content strong {
          color: var(--text-primary);
        }
        
        /* Takeaways */
        .takeaways-section {
          background: var(--bg-paper);
        }
        
        .takeaways-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
        }
        
        .takeaway-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px 20px;
          background: var(--bg-default);
          border-radius: 8px;
        }
        
        .takeaway-item svg {
          color: var(--success);
          flex-shrink: 0;
        }
        
        .takeaway-item span {
          color: var(--text-secondary);
          font-size: 14px;
        }
        
        /* CTA */
        .cta-section .cta-card {
          text-align: center;
          padding: 64px;
          background: linear-gradient(135deg, rgba(255, 215, 0, 0.05), rgba(184, 134, 11, 0.05));
          border: 1px solid rgba(255, 215, 0, 0.2);
          border-radius: 24px;
        }
        
        .cta-icon {
          color: var(--gold);
          margin-bottom: 24px;
        }
        
        .cta-card h2 {
          margin-bottom: 12px;
        }
        
        .cta-card p {
          color: var(--text-secondary);
          max-width: 500px;
          margin: 0 auto 32px;
        }
        
        /* Loading */
        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 60vh;
          color: var(--text-muted);
        }
        
        .spinner {
          width: 40px;
          height: 40px;
          border: 3px solid var(--border);
          border-top-color: var(--gold);
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 16px;
        }
        
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        
        @media (max-width: 1024px) {
          .facts-grid {
            grid-template-columns: 1fr;
          }
        }
        
        @media (max-width: 768px) {
          .gold-hero h1 {
            font-size: 2.5rem;
          }
          
          .gold-price-card {
            flex-direction: column;
            text-align: center;
          }
          
          .takeaways-grid {
            grid-template-columns: 1fr;
          }
          
          .cta-card {
            padding: 32px;
          }
        }
      `}</style>
    </div>
  );
};

export default GoldGuide;